import { Builder, BuilderBlocks, BuilderStore } from '@builder.io/react';
import { BuilderBlock } from '@builder.io/react/dist/types/src/components/builder-block.component';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { BuilderContextProps } from 'shared-react-components';
import CheckBox from '../icons/checkbox';
import CheckBoxChecked from '../icons/checkbox-checked';
import Vector from '../icons/vector';
import './variant-accordion.css';

export interface BuilderSectionProps {
  foldTitle: string;
  productSku: string;
  alternateProductSku: string;
  freeShipping: boolean;
  content?: ReactNode;
  model?: string;
  data?: any;
  attributes: {
    style: any;
    className: string;
    'builder-id': string;
    id: string;
  };
}

interface VariantAccordionProps {
  builderContext?: BuilderContextProps;
  context?: any;
  builderState?: BuilderStore;
  productName: string;
  currentVariantSku: string;
  folds: BuilderSectionProps[];
  builderBlock: BuilderBlock;
  attributes: {
    style: React.CSSProperties;
    className: string;
    'builder-id': string;
    id: string;
  };
}

function getBuilderContextAction<T>(action: string, builderState: any) {
  if (builderState && builderState.context && action in builderState.context) {
    const contextAction = builderState.context[action];
    if (contextAction && typeof contextAction === 'function') {
      return builderState.context[action] as T;
    }
  }
}

export const VariantAccordion: React.FC<VariantAccordionProps> = ({
  builderState,
  currentVariantSku,
  folds,
  builderBlock,
  attributes
}) => {
  const [activeFolds, setActiveFolds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const contentRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isEditing = Builder.isEditing;

  const swapProduct = getBuilderContextAction<
    (sku: string, quantity: number) => Promise<boolean | undefined>
  >('swapProduct', builderState);

  useEffect(() => {
    const defaultActiveFold = folds.findIndex((fold) => fold.productSku.trim() === currentVariantSku || fold.alternateProductSku?.trim() === currentVariantSku);
    if (defaultActiveFold !== -1 && !isEditing) {
      setActiveFolds([defaultActiveFold]);
    } else if (isEditing) {
      setActiveFolds(folds.map((_, index) => index));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVariantSku, isEditing]);

  const clickHandler = async (index: number) => {
    if (loading) return;

    if (isEditing) {
      setActiveFolds((prevActiveFolds) =>
        prevActiveFolds.includes(index)
          ? prevActiveFolds.filter((i) => i !== index)
          : [...prevActiveFolds, index]
      );
    } else if (!activeFolds.includes(index) && swapProduct && folds[index]) {
        const previousActiveFolds = [...activeFolds];
        try {
          setLoading(true);
          setActiveFolds([index]);
          const result = await swapProduct(folds[index]!.productSku, 1);
          if (!result) {
            throw new Error('Swap product failed');
          }
        } catch (error) {
          console.error('Error swapping product:', error);
          setActiveFolds(previousActiveFolds);
        } finally {
          setLoading(false);
        }
    }
  };

  // animation
  useEffect(() => {
    if (isEditing) {
      folds.forEach((_, index) => {
        const content = contentRefs.current[index];
        if (content) {
          content.style.maxHeight = activeFolds.includes(index) ? `${content.scrollHeight}px` : '0';
        }
      });
    } else if (activeFolds.length) {
      activeFolds.forEach((index) => {
        const content = contentRefs.current[index];
        if (content) {
          content.style.maxHeight = `${content.scrollHeight}px`;
        }
      });
    }
  }, [activeFolds, isEditing, folds]);

  const getFoldClasses = (index: number) => {
    const isActive = activeFolds.includes(index);
    const inactiveSequences: number[][] = [];
    let sequence: number[] = [];

    folds.forEach((_, i) => {
      if (activeFolds.includes(i)) {
        if (sequence.length) {
          inactiveSequences.push([...sequence]);
          sequence = [];
        }
      } else {
        sequence.push(i);
      }
    });

    if (sequence.length) {
      inactiveSequences.push([...sequence]);
    }

    let first = false;
    let last = false;

    inactiveSequences.forEach((seq) => {
      if (seq.includes(index)) {
        if (index === seq[0]) first = true;
        if (index === seq[seq.length - 1]) last = true;
      }
    });

    return `${isActive ? 'active' : 'inactive'} ${first ? 'first' : ''} ${last ? 'last' : ''}`;
  };

  return (
    <div
      style={attributes.style}
      className={`${attributes.className} variant-accordion`}
      builder-id={attributes['builder-id']}
      id={attributes.id}
    >
      {folds?.map((fold, index) => (
        <div
          id={`${attributes['builder-id']}-${index}`}
          key={index}
          className={`builder-fold ${getFoldClasses(index)}`}
        >
          <div className="builder-fold--info" onClick={() => clickHandler(index)}>
            {loading && <div className="loadingOverlay" />}
            <div className="checkbox">
              {activeFolds.includes(index) ? <CheckBoxChecked /> : <CheckBox />}
            </div>            
            <div className="builder-fold--rte cql-formatted-text" dangerouslySetInnerHTML={{__html: fold.foldTitle}} />
            {fold.freeShipping && (
              <div className="free-shipping-badge">
                <Vector />
                <span className="text">FREE S&H</span>
              </div>
            )}
          </div>
          <div
            ref={(el) => (contentRefs.current[index] = el)}
            className="fold-content"
            style={{
              maxHeight: activeFolds.includes(index)
                ? `${contentRefs.current[index]?.scrollHeight}px`
                : '0'
            }}
          >
            <BuilderBlocks
              parentElementId={builderBlock.id}
              dataPath={`component.options.folds.${index}.content`}
              blocks={fold.content}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
